import { useEffect, useRef, useState } from 'react';
import { Copy, Download, Loader2, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  buildAddressBookCsvFileState,
  buildEditAddressBookInput,
  buildFailedAddressBookCsvFileState,
  type AddressBookCsvFileState,
} from '@/lib/address-book/import-address-book-csv';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useEditAddressBook } from '@/hooks/organization/use-edit-address-book';
import { getRequestErrorMessage } from '@/lib/api/get-request-error-message';
import { cn } from '@/lib/utils';
const EXAMPLE_CSV = [
  'name,address',
  'Treasury Safe,0x8F4d3c91d0A60000000000000000000000009A21',
  'Payroll Ops,0xA7cE8a84B2d100000000000000000000000041F3',
  'Market Makers,0x1B29E5a2f8C40000000000000000000000000D77',
].join('\n');

const EXAMPLE_CSV_PREVIEW = [
  'name,address',
  'Treasury Safe,0x8F4d3c91d0A6...9A21',
  'Payroll Ops,0xA7cE8a84B2d1...41F3',
  'Market Makers,0x1B29E5a2f8C4...0D77',
].join('\n');

interface ImportAddressBookCsvDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported: () => void;
}

export function ImportAddressBookCsvDialog({
  open,
  onOpenChange,
  onImported,
}: ImportAddressBookCsvDialogProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [fileState, setFileState] = useState<AddressBookCsvFileState | null>(
    null
  );
  const [importErrorMessage, setImportErrorMessage] = useState<string | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { editAddressBook, isLoading } = useEditAddressBook();

  useEffect(() => {
    if (!open) {
      setIsDragging(false);
      setFileState(null);
      setImportErrorMessage(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [open]);

  const handleCopyExample = async () => {
    try {
      await navigator.clipboard.writeText(EXAMPLE_CSV);
    } catch {
      // Copy failures are non-fatal.
    }
  };

  const handleDownloadExample = () => {
    let url: string | null = null;

    try {
      const blob = new Blob([EXAMPLE_CSV], {
        type: 'text/csv;charset=utf-8',
      });
      url = URL.createObjectURL(blob);

      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'address-book-example.csv';
      anchor.click();
    } catch {
      // Example download failures are non-fatal.
    } finally {
      if (url) {
        URL.revokeObjectURL(url);
      }
    }
  };

  const parseFile = async (file: File) => {
    setImportErrorMessage(null);

    try {
      const contents = await file.text();
      setFileState(buildAddressBookCsvFileState(file.name, contents));
    } catch (error) {
      setFileState(buildFailedAddressBookCsvFileState(file.name, error));
    }
  };

  const handleFiles = async (files: FileList | File[]) => {
    const firstFile = Array.from(files)[0];

    if (!firstFile) {
      return;
    }

    if (
      !firstFile.name.toLowerCase().endsWith('.csv') &&
      firstFile.type !== 'text/csv'
    ) {
      setImportErrorMessage(null);
      setFileState(
        buildFailedAddressBookCsvFileState(
          firstFile.name,
          new Error('Please upload a CSV file.')
        )
      );
      return;
    }

    await parseFile(firstFile);
  };

  const handleImport = async () => {
    if (!fileState || fileState.status !== 'ready' || isLoading) {
      return;
    }

    setImportErrorMessage(null);

    try {
      await editAddressBook(buildEditAddressBookInput(fileState.instructions));
      onImported();
      onOpenChange(false);
    } catch (error) {
      setImportErrorMessage(
        getRequestErrorMessage(error, 'Failed to import address labels.')
      );
    }
  };

  const uploadedCard =
    fileState?.status === 'ready' ? (
      <section className="rounded-[12px] border border-[#bbf7d0] bg-[#f0fdf4] px-5 py-5">
        <div className="flex items-start justify-between gap-3">
          <p className="min-w-0 flex-1 truncate text-[16px] font-semibold leading-[23px] text-[#18181b]">
            {fileState.fileName}
          </p>
          <div className="shrink-0 rounded-full bg-[#dcfce7] px-2 py-1 text-[12px] font-medium leading-[17px] text-[#166534]">
            Ready to import
          </div>
        </div>
        <p className="mt-3 text-[14px] leading-[20px] text-[#52525b]">
          {fileState.rowCount} address{' '}
          {fileState.rowCount === 1 ? 'label' : 'labels'} detected
        </p>
        <Button
          type="button"
          variant="outline"
          className="mt-4 h-[37px] rounded-[9px] border-[#e4e4e7] bg-white px-[19px] py-[6px] text-[14px] font-medium text-[#18181b] shadow-none hover:bg-white"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="mr-2 h-[15px] w-[15px]" />
          Upload a different file
        </Button>
      </section>
    ) : fileState?.status === 'failed' ? (
      <section className="rounded-[12px] border border-[#fecaca] bg-[#fef2f2] px-5 py-5">
        <div className="flex items-start justify-between gap-3">
          <p className="min-w-0 flex-1 truncate text-[16px] font-semibold leading-[23px] text-[#18181b]">
            {fileState.fileName}
          </p>
          <div className="shrink-0 rounded-full bg-[#fee2e2] px-2 py-1 text-[12px] font-medium leading-[17px] text-[#b91c1c]">
            Formatting error
          </div>
        </div>
        <div className="mt-3 space-y-0.5 text-[13px] leading-[19px] text-[#dc2626]">
          {fileState.errorMessage.split('\n').map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
        <Button
          type="button"
          className="mt-4 h-[37px] rounded-[9px] bg-[#18181b] px-[19px] py-[6px] text-[14px] font-medium text-white hover:bg-[#18181b]"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="mr-2 h-[15px] w-[15px]" />
          Upload another file
        </Button>
      </section>
    ) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[92vw] max-w-[624px] max-h-[88vh] gap-0 overflow-hidden rounded-[15px] border border-[#e2e8f0] p-0 shadow-[0px_4px_6px_-3px_rgba(0,0,0,0.1)] [&>button]:hidden">
        <DialogHeader className="px-5 pt-5 pb-0 text-left">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 space-y-4">
              <DialogTitle className="text-[16px] font-semibold leading-none text-[#52525b]">
                Upload a custom CSV file
              </DialogTitle>
              <DialogDescription className="text-[14px] leading-[14.2px] text-black">
                Add or update address book labels in bulk from a single CSV
              </DialogDescription>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="mt-[-4px] h-[48px] w-[48px] shrink-0 rounded-full p-[15px] text-[#52525b]"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-[18px] w-[18px]" />
              <span className="sr-only">Close</span>
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-5 overflow-y-auto px-8 pb-5 pt-5">
          <section className="overflow-hidden rounded-[14px] border border-white bg-[#f4f4f5] shadow-[0px_4px_6px_-3px_rgba(0,0,0,0.1)]">
            <div className="flex items-center justify-between px-5 py-[7px]">
              <p className="text-[12px] font-normal leading-none text-[#6b7280]">
                Example CSV
              </p>
              <div className="flex items-center gap-[6px]">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-[12px] w-[12px] rounded-none p-0 text-[#6b7280] hover:bg-transparent"
                  onClick={() => void handleCopyExample()}
                >
                  <Copy className="h-[12px] w-[12px]" />
                  <span className="sr-only">Copy example CSV</span>
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-[12px] w-[12px] rounded-none p-0 text-[#6b7280] hover:bg-transparent"
                  onClick={handleDownloadExample}
                >
                  <Download className="h-[12px] w-[12px]" />
                  <span className="sr-only">Download example CSV</span>
                </Button>
              </div>
            </div>
            <div className="px-0 pb-0">
              <pre className="overflow-x-auto rounded-[14px] border border-white bg-[#fafafa] px-5 py-5 text-[12px] font-medium leading-normal text-black shadow-none whitespace-pre-wrap break-all">
                {EXAMPLE_CSV_PREVIEW}
              </pre>
            </div>
          </section>

          {uploadedCard ? (
            uploadedCard
          ) : (
            <section
              className={cn(
                'rounded-[10px] border border-dashed bg-[#f4f4f5] transition-colors',
                isDragging
                  ? 'border-[#a1a1aa] bg-[#ededf0]'
                  : 'border-[#e4e4e7]'
              )}
              onDragEnter={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setIsDragging(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setIsDragging(false);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onDrop={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setIsDragging(false);
                void handleFiles(event.dataTransfer.files);
              }}
            >
              <div className="flex h-[230px] flex-col items-center justify-center gap-5 px-5 py-5 text-center">
                <p className="text-[16px] font-semibold leading-none text-black">
                  Drag CSV file here
                </p>
                <p className="text-[16px] font-normal leading-none text-black">
                  or
                </p>
                <Button
                  type="button"
                  className="h-[37px] rounded-[9px] bg-[#18181b] px-[19px] py-[6px] text-[14px] font-semibold text-white hover:bg-[#18181b]"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="mr-2 h-[15px] w-[15px]" />
                  Upload from computer
                </Button>
              </div>
            </section>
          )}

          {importErrorMessage ? (
            <div className="rounded-[12px] border border-[#fecaca] bg-[#fef2f2] px-5 py-4 text-[13px] leading-[19px] text-[#dc2626]">
              {importErrorMessage}
            </div>
          ) : null}

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(event) => {
              void handleFiles(event.target.files ?? []);
            }}
          />

          <DialogFooter className="gap-3 pt-1 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
              className="h-[40px] rounded-[8px] border-[#e4e4e7] bg-white px-4 py-[10px] text-[14px] font-medium text-[#18181b] hover:bg-white"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleImport()}
              disabled={fileState?.status !== 'ready' || isLoading}
              className="relative h-[40px] rounded-[8px] bg-[#18181b] px-4 py-[10px] text-[14px] font-medium text-[#fafafa] hover:bg-[#18181b] disabled:opacity-50"
            >
              <span className={isLoading ? 'invisible' : ''}>
                Import labels
              </span>
              {isLoading ? (
                <span className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </span>
              ) : null}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
